import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useAppHistory } from "./useAppHistory";

const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/** Shown in native `title` tooltips next to the controls (matches the handler below). */
export const shortcuts = {
  back: isMac ? "⌘[ or ⌥←" : "Ctrl+[ or Alt+←",
  forward: isMac ? "⌘] or ⌥→" : "Ctrl+] or Alt+→",
  commandPalette: isMac ? "⌘K" : "Ctrl+K",
  chatHome: isMac ? "⌘⇧O" : "Ctrl+Shift+O",
  settings: isMac ? "⌘," : "Ctrl+,",
} as const;

export const commandPaletteKey = isMac ? "⌘K" : "Ctrl+K";

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return target.isContentEditable;
}

/**
 * The app's keyboard shortcuts: the command palette, chat home, settings,
 * and back and forward. AppLayout mounts it once, whichever top bar is showing.
 */
export function useAppShortcuts(onToggleCommandPalette: () => void) {
  const navigate = useNavigate();
  const { back, forward } = useAppHistory();

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const isMeta = event.metaKey || event.ctrlKey;

      // The palette opens from anywhere, a text field included.
      if (isMeta && (event.key === "k" || event.key === "K")) {
        event.preventDefault();
        onToggleCommandPalette();
        return;
      }

      if (isEditableTarget(event.target)) return;

      if (isMeta && event.shiftKey && (event.key === "o" || event.key === "O")) {
        event.preventDefault();
        navigate("/");
        return;
      }

      if (isMeta && event.key === ",") {
        event.preventDefault();
        navigate("/settings");
        return;
      }

      if (isMeta && event.key === "[") {
        event.preventDefault();
        back();
        return;
      }
      if (isMeta && event.key === "]") {
        event.preventDefault();
        forward();
        return;
      }
      if (event.altKey && !event.metaKey && !event.ctrlKey) {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          back();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          forward();
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [back, forward, navigate, onToggleCommandPalette]);
}
